import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface TableSortState {
  key: string | null;
  direction: SortDirection;
}

export interface TableSortControl {
  sort: TableSortState;
  toggle: (key: string) => void;
}

export interface TableSortResult<T> extends TableSortControl {
  rows: T[];
}

/** Per-column value extractors used while comparing rows. */
export type SortAccessors<T> = Partial<Record<string, (row: T) => unknown>>;

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2}))?/;
const SLASH_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*(\d{1,2}):(\d{2})\s*(am|pm))?/i;

/** Parses dates shaped like ISO strings or the app's DD/MM/YYYY[, hh:mm am/pm] display format. */
const parseSortableDate = (value: string): number | null => {
  const iso = ISO_DATE.exec(value);
  if (iso) {
    const [, year, month, day, hours = '0', minutes = '0'] = iso;
    return new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes)).getTime();
  }
  const slash = SLASH_DATE.exec(value);
  if (slash) {
    const [, day, month, year, hours = '0', minutes = '0', meridiem] = slash;
    let hour = Number(hours);
    if (meridiem) {
      const afternoon = meridiem.toLowerCase() === 'pm';
      if (afternoon && hour < 12) hour += 12;
      if (!afternoon && hour === 12) hour = 0;
    }
    return new Date(Number(year), Number(month) - 1, Number(day), hour, Number(minutes)).getTime();
  }
  return null;
};

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === '';

const compareValues = (left: unknown, right: unknown): number => {
  if (isEmptyValue(left)) return isEmptyValue(right) ? 0 : 1;
  if (isEmptyValue(right)) return -1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  if (typeof left === 'boolean' && typeof right === 'boolean') return Number(left) - Number(right);
  const leftText = String(left).trim();
  const rightText = String(right).trim();
  const leftTime = parseSortableDate(leftText);
  const rightTime = parseSortableDate(rightText);
  if (leftTime !== null && rightTime !== null && leftTime !== rightTime) return leftTime - rightTime;
  return collator.compare(leftText, rightText);
};

const cellValue = (row: unknown, key: string): unknown => (row as Record<string, unknown>)[key];

/**
 * Table sorting helper with two modes:
 *
 * 1. Uncontrolled client-side sorting — pass the rows (and optional per-column
 *    accessors); clicking a header sorts the given rows in memory. Clicking the
 *    same column toggles between ascending and descending; a new column starts
 *    ascending.
 * 2. Controlled server-backed sorting — pass the current sort column and
 *    direction plus an onChange callback; the caller owns persistence (e.g.
 *    query params refetched from the API) and simply re-renders with new rows.
 */
export function useTableSort<T>(rows: readonly T[] | undefined, accessors?: SortAccessors<T>): TableSortResult<T>;
export function useTableSort(
  sortKey: string | null,
  sortDirection: SortDirection,
  onSortChange: (next: TableSortState) => void,
): TableSortControl;
export function useTableSort<T>(
  rowsOrKey: readonly T[] | undefined | string | null,
  accessorsOrDirection?: SortAccessors<T> | SortDirection,
  onSortChange?: (next: TableSortState) => void,
): TableSortResult<T> | TableSortControl {
  const isControlled = rowsOrKey === null || typeof rowsOrKey === 'string';
  const [internalState, setInternalState] = useState<TableSortState>({ key: null, direction: 'asc' });

  const controlledKey = isControlled ? rowsOrKey : null;
  const controlledDirection: SortDirection = isControlled
    ? ((accessorsOrDirection as SortDirection | undefined) ?? 'asc')
    : 'asc';
  const clientRows = isControlled ? undefined : rowsOrKey;
  const clientAccessors = isControlled ? undefined : (accessorsOrDirection as SortAccessors<T> | undefined);

  const sort: TableSortState = isControlled
    ? { key: controlledKey, direction: controlledDirection }
    : internalState;

  const toggle = useCallback((key: string) => {
    if (isControlled) {
      const nextDirection: SortDirection = key === controlledKey && controlledDirection === 'asc' ? 'desc' : 'asc';
      onSortChange?.({ key, direction: nextDirection });
      return;
    }
    setInternalState((current) => (current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }));
  }, [isControlled, controlledKey, controlledDirection, onSortChange]);

  const sortedRows = useMemo<T[]>(() => {
    if (!clientRows) return [];
    const key = sort.key;
    if (!key) return [...clientRows];
    const accessor = clientAccessors?.[key];
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...clientRows].sort((left, right) => factor * compareValues(
      accessor ? accessor(left) : cellValue(left, key),
      accessor ? accessor(right) : cellValue(right, key),
    ));
  }, [clientRows, clientAccessors, sort.key, sort.direction]);

  return { rows: sortedRows, sort, toggle };
}
